import React, { Component } from 'react';
import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from 'react-router-dom';
import { MainContainer } from '../Styles/MainContainer';
import { AlertsContainer, AppContainer } from '../Styles/AppContainer';
import { AppProps } from '../Types/AppProps';
import TopBar from './TopBar';
import i18next from 'i18next';
import LanguageContext from '../../../__core/Providers/LanguageContext';
import { withTranslation, WithTranslation } from 'react-i18next';
import NotFound from '../../../__core/Components/NotFound/NotFound';
import { fetchLang } from '../Actions/Actions';
import { changeLanguage } from '../../../__core/Utils/Language';
import { BackToTop } from '../Styles/BackToTop';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons';
import Forbidden from '../../../__core/Components/Forbidden/Forbidden';
import { debounce, get } from 'lodash';
import SystemModuleLoader from '../../../__core/Components/Loader/SystemModuleLoader';
import { GITHUB_ALERT_STATUS } from '../../../__core/Utils/Constraints';
import CoursesListContainer from '../../Courses/Containers/CoursesListContainer';
import MobileMenu from './MobileMenu/MobileMenu';
import Material from '../../Material/Containers/MaterialContainer';
import REDIRECT_TYPES from '../../../__core/Enums/RedirectTypes';
import { getBaseUrlForRedirect } from '../../../__core/Utils/GetBaseUrl';
import AlertDanger from '../../../__core/Components/Alerts/AlertDanger';
import { GITHUB_ACCOUNT_LINKED_PREFIX } from '../../CourseInstance/Constraints';
import GitHubRedirect from '../../../__core/Providers/GitHub/GitHubRedirect';
import InternalServerError from '../../../__core/Components/InternalServerError/InternalServerError';
import RegisterInfoModal from './RegisterInfoModal/RegisterInfoModal';
import AuthRoute from '../../../__core/Providers/Auth/AuthRoute';
import PrivateComponent from '../../../__core/Providers/Auth/PrivateComponent';
import { getToken } from '../../../__core/Providers/Token/GetToken';
import 'react-tippy/dist/tippy.css';

const DirectAccess = React.lazy(
  () => import('../../DirectAccess/Containers/DirectAccess')
);

const Course = React.lazy(
  () => import('../../Course/Containers/CourseDetailsContainer')
);

const CourseInstance = React.lazy(
  () => import('../../CourseInstance/Containers/CourseInstanceContainer')
);

const DirectAccessMaterial = React.lazy(
  () => import('../../DirectAccess/Containers/Material')
);

const Register = React.lazy(() => import('../../Register/Containers/Register'));

type AppState = {
  language: string;
  backToTopVisible: boolean;
};

const GO_TO_TOP_DELAY = 500;

class App extends Component<WithTranslation & AppProps, AppState> {
  private readonly mainContainerRef: React.RefObject<HTMLElement>;
  constructor(props: WithTranslation & AppProps) {
    super(props);
    this.state = {
      language: i18next.language,
      backToTopVisible: false,
    };

    i18next.on('languageChanged', this.onChangeLanguage);
    this.mainContainerRef = React.createRef();
  }

  componentDidMount(): void {
    this.getData();

    if (this.mainContainerRef && this.mainContainerRef.current) {
      this.mainContainerRef.current.addEventListener(
        'scroll',
        this.toggleVisibility
      );
    }
  }

  componentDidUpdate(prevProps: Readonly<WithTranslation & AppProps>): void {
    const { token } = this.props;
    const { token: prevToken } = prevProps;

    if (!!token && prevToken !== token) {
      this.getData();
    }
  }

  componentWillUnmount(): void {
    if (this.mainContainerRef && this.mainContainerRef.current) {
      this.mainContainerRef.current.removeEventListener(
        'scroll',
        this.toggleVisibility
      );
    }
  }

  getLanguageFromLocalStorage = (): void => {
    const lang = window.localStorage.getItem('appLang');
    if (lang) {
      changeLanguage(lang);
    }
  };

  getData = (): void => {
    const { actions, token } = this.props;
    if (token) {
      actions.fetchUser();
      this.getLanguageFromApi();
    } else {
      this.getLanguageFromLocalStorage();
    }
  };

  getRedirectUrl = (userRedirect: {
    payload: unknown | null;
    type: REDIRECT_TYPES | null;
  }): string => {
    if (userRedirect.type === REDIRECT_TYPES.MATERIAL) {
      const instanceId = get(userRedirect, 'payload.instanceId');
      const materialId = get(userRedirect, 'payload.materialId');
      const chapterId = get(userRedirect, 'payload.chapterId');
      return `/material/${instanceId}/${chapterId}/${materialId}`;
    } else if (userRedirect.type === REDIRECT_TYPES.LIST) {
      return '/courses';
    } else {
      return '/';
    }
  };

  toggleVisibility = debounce((): void => {
    if (this.mainContainerRef && this.mainContainerRef.current) {
      const scrollPixelOffset = 400;
      const { scrollTop } = this.mainContainerRef.current;
      const { backToTopVisible } = this.state;

      if (scrollTop > scrollPixelOffset && !backToTopVisible) {
        this.setState({ backToTopVisible: true });
      } else if (scrollTop <= scrollPixelOffset && backToTopVisible) {
        this.setState({ backToTopVisible: false });
      }
    }
  }, GO_TO_TOP_DELAY);

  scrollToTop = (): void => {
    if (this.mainContainerRef && this.mainContainerRef.current) {
      this.mainContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  onChangeLanguage = (language: string): void => this.setState({ language });

  getLanguageFromApi = async (): Promise<void> => {
    const lang: string = await fetchLang();
    if (lang) {
      changeLanguage(lang);
    }
  };

  shouldShowGitHubAlert = (): boolean => {
    const {
      user: { showGithubWarning, github },
    } = this.props;

    if (showGithubWarning && !github) {
      const gitHubAlertStatus = sessionStorage.getItem(GITHUB_ALERT_STATUS);
      return !gitHubAlertStatus;
    }

    return false;
  };

  handleOnCloseAlert = (): void =>
    sessionStorage.setItem(GITHUB_ALERT_STATUS, 'close');

  render(): JSX.Element {
    const {
      user,
      t,
      actions: { fetchUser },
    } = this.props;
    const { language, backToTopVisible } = this.state;
    const token = getToken();

    const alertContent = (
      <span>
        {t(
          'Connect your account with GitHub to have access for course materials and solve exercise. Log in to your GitHub account or create new - click'
        )}
        &nbsp;
        <a href={`${getBaseUrlForRedirect()}/auth/github_connect`}>
          {t('here')}
        </a>
      </span>
    );

    return (
      <LanguageContext.Provider value={language}>
        <Router>
          <TopBar user={user} refreshUserData={fetchUser} />
          <PrivateComponent>
            <MobileMenu user={user} refreshUserData={fetchUser} />
          </PrivateComponent>
          <RegisterInfoModal />
          <AppContainer>
            <React.Suspense fallback={<SystemModuleLoader />}>
              <MainContainer>
                <main ref={this.mainContainerRef} id="main-app-container">
                  <AlertsContainer>
                    <AlertDanger
                      show={this.shouldShowGitHubAlert()}
                      onClose={this.handleOnCloseAlert}
                      message={alertContent}
                    />
                  </AlertsContainer>
                  <Switch>
                    {token ? (
                      <Redirect from="/signup" to="/" />
                    ) : (
                      <Route exact path="/signup" component={Register} />
                    )}

                    <AuthRoute exact path="/courses">
                      <CoursesListContainer />
                    </AuthRoute>

                    {token && (
                      <Redirect
                        exact
                        from="/"
                        to={this.getRedirectUrl(user.redirect)}
                      />
                    )}

                    <AuthRoute
                      exact
                      path="/material/:instanceId/:slug/:materialId?"
                    >
                      <Material />
                    </AuthRoute>

                    <AuthRoute exact path="/course/:signature">
                      <Course />
                    </AuthRoute>

                    <AuthRoute
                      exact
                      path="/course-instance/:signature/:activeTab?"
                    >
                      <CourseInstance />
                    </AuthRoute>

                    <AuthRoute
                      exact
                      path="/direct-access/:technologyId/:version?"
                    >
                      <DirectAccess />
                    </AuthRoute>

                    <AuthRoute
                      exact
                      path="/direct-access-material/:version/:courseId/:chapterId/:materialId?"
                    >
                      <DirectAccessMaterial />
                    </AuthRoute>

                    <AuthRoute path={`/${GITHUB_ACCOUNT_LINKED_PREFIX}`}>
                      <GitHubRedirect />
                    </AuthRoute>

                    <AuthRoute exact path="/403" component={Forbidden} />

                    <AuthRoute exact path="/404" component={NotFound} />

                    <AuthRoute
                      exact
                      path="/500"
                      component={InternalServerError}
                    />

                    <AuthRoute exact path="*" component={NotFound} />
                  </Switch>

                  {backToTopVisible && (
                    <BackToTop
                      onClick={this.scrollToTop}
                      title={t('Back to top')}
                    >
                      <FontAwesomeIcon icon={faArrowUp} />
                    </BackToTop>
                  )}
                </main>
              </MainContainer>
            </React.Suspense>
          </AppContainer>
        </Router>
      </LanguageContext.Provider>
    );
  }
}

export default withTranslation()(App);
